/**
 * Hand-written saas-auth wire types for the tenant whitelabel-domain surface
 * (SASATH-0332, transcribed from `dto/saasadmin/*.java` @ `d4444ce6`), served to this console at
 * `/api/reseller-admin/tenants/{tenantId}/whitelabel-domain` (TMMRSL-0030).
 * saas-auth publishes no OpenAPI file. Normalise defensively at the module
 * boundary — never trust missing arrays. The normalizers take the UNWRAPPED body
 * (`unwrap()` in `saasAuthReseller.ts` strips the `ApiResponse` envelope).
 */

export type HostnameType =
  | 'APP'
  | 'LOGIN'
  | 'AUTH'
  | 'BRAND'
  | 'ASSET'
  | 'TENANT_ADMIN'
  | (string & {});

export type WhitelabelDomainState = 'DRAFT' | 'ACTIVE';

export interface WhitelabelTemplateHost {
  prefix: string;
  hostnameType: HostnameType;
  appId?: number;
  appSlug?: string;
  note?: string;
}

export interface WhitelabelTemplateDto {
  hosts: WhitelabelTemplateHost[];
}

export interface PutWhitelabelHost {
  prefix: string;
  hostnameType: HostnameType;
  appId?: number;
}

export interface PutWhitelabelDomainRequest {
  domain: string;
  hosts: PutWhitelabelHost[];
}

export interface WhitelabelHostname {
  id: number;
  prefix: string;
  hostname: string;
  hostnameType: HostnameType;
  appId?: number;
  appSlug?: string;
  dnsVerified: boolean;
  dnsVerifiedAt?: string;
}

export interface WhitelabelDnsRecord {
  host: string;
  type: string;
  target: string;
  dnsVerified: boolean;
}

export interface WhitelabelDomainDto {
  tenantId: number;
  configured: boolean;
  domain?: string;
  state?: WhitelabelDomainState;
  activatedAt?: string;
  hostnames: WhitelabelHostname[];
  dnsConfigured: boolean;
  dnsRecords: WhitelabelDnsRecord[];
}

export interface WhitelabelVerifyResult {
  prefix: string;
  hostname: string;
  hostnameType: HostnameType;
  matched: boolean;
  dnsVerified: boolean;
}

export interface WhitelabelVerifyResponse {
  tenantId: number;
  domain?: string;
  state: WhitelabelDomainState;
  activatedAt?: string;
  allVerified: boolean;
  results: WhitelabelVerifyResult[];
  dnsConfigured: boolean;
  dnsRecords: WhitelabelDnsRecord[];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asHostnameType(value: unknown): HostnameType {
  return typeof value === 'string' && value.length > 0 ? value : '';
}

function asState(value: unknown): WhitelabelDomainState | undefined {
  return value === 'DRAFT' || value === 'ACTIVE' ? value : undefined;
}

function normalizeHostname(raw: unknown): WhitelabelHostname {
  const row = asRecord(raw);
  const out: WhitelabelHostname = {
    id: asNumber(row.id) ?? 0,
    prefix: asString(row.prefix) ?? '',
    hostname: asString(row.hostname) ?? '',
    hostnameType: asHostnameType(row.hostnameType),
    dnsVerified: row.dnsVerified === true,
  };
  const appId = asNumber(row.appId);
  if (appId !== undefined) out.appId = appId;
  const appSlug = asString(row.appSlug);
  if (appSlug !== undefined) out.appSlug = appSlug;
  const dnsVerifiedAt = asString(row.dnsVerifiedAt);
  if (dnsVerifiedAt !== undefined) out.dnsVerifiedAt = dnsVerifiedAt;
  return out;
}

function normalizeDnsRecord(raw: unknown): WhitelabelDnsRecord {
  const row = asRecord(raw);
  return {
    host: asString(row.host) ?? '',
    type: asString(row.type) ?? 'CNAME',
    target: asString(row.target) ?? '',
    dnsVerified: row.dnsVerified === true,
  };
}

export function emptyDomain(): WhitelabelDomainDto {
  return {
    tenantId: 0,
    configured: false,
    hostnames: [],
    dnsConfigured: false,
    dnsRecords: [],
  };
}

export function normalizeDomain(body: unknown): WhitelabelDomainDto {
  const raw = asRecord(body);
  const out: WhitelabelDomainDto = {
    tenantId: asNumber(raw.tenantId) ?? 0,
    configured: raw.configured === true,
    hostnames: Array.isArray(raw.hostnames) ? raw.hostnames.map(normalizeHostname) : [],
    dnsConfigured: raw.dnsConfigured === true,
    dnsRecords: Array.isArray(raw.dnsRecords) ? raw.dnsRecords.map(normalizeDnsRecord) : [],
  };
  const domain = asString(raw.domain);
  if (domain !== undefined) out.domain = domain;
  const state = asState(raw.state);
  if (state !== undefined) out.state = state;
  const activatedAt = asString(raw.activatedAt);
  if (activatedAt !== undefined) out.activatedAt = activatedAt;
  return out;
}

export function normalizeTemplate(body: unknown): WhitelabelTemplateDto {
  const raw = asRecord(body);
  const hosts = Array.isArray(raw.hosts) ? raw.hosts : [];
  return {
    hosts: hosts.map((item) => {
      const row = asRecord(item);
      const host: WhitelabelTemplateHost = {
        prefix: asString(row.prefix) ?? '',
        hostnameType: asHostnameType(row.hostnameType),
      };
      const appId = asNumber(row.appId);
      if (appId !== undefined) host.appId = appId;
      const appSlug = asString(row.appSlug);
      if (appSlug !== undefined) host.appSlug = appSlug;
      const note = asString(row.note);
      if (note !== undefined) host.note = note;
      return host;
    }),
  };
}

export function normalizeVerify(body: unknown): WhitelabelVerifyResponse {
  const raw = asRecord(body);
  const results = Array.isArray(raw.results) ? raw.results : [];
  const out: WhitelabelVerifyResponse = {
    tenantId: asNumber(raw.tenantId) ?? 0,
    state: asState(raw.state) ?? 'DRAFT',
    allVerified: raw.allVerified === true,
    results: results.map((item) => {
      const row = asRecord(item);
      return {
        prefix: asString(row.prefix) ?? '',
        hostname: asString(row.hostname) ?? '',
        hostnameType: asHostnameType(row.hostnameType),
        matched: row.matched === true,
        dnsVerified: row.dnsVerified === true,
      };
    }),
    dnsConfigured: raw.dnsConfigured === true,
    dnsRecords: Array.isArray(raw.dnsRecords) ? raw.dnsRecords.map(normalizeDnsRecord) : [],
  };
  const domain = asString(raw.domain);
  if (domain !== undefined) out.domain = domain;
  const activatedAt = asString(raw.activatedAt);
  if (activatedAt !== undefined) out.activatedAt = activatedAt;
  return out;
}
